import { themeColors } from "../themeColors";
/**
 * @file  src\pages\AgentDashboard.js
 * @brief  This file is responsible for agent dashboard section
 * @date   April, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */
import React, { useState, useRef, useMemo } from "react";
import { useSelector } from "react-redux";
import Button from "react-bootstrap/Button";
import { Tabs, Tab } from "react-bootstrap";
import Dropdown from "react-bootstrap/Dropdown";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import AddNewClientModal from "../components/MemberWantAd/AddNewClientModal";
import DeleteModal from "../components/Common/DeleteModal";
import { useNavigate } from "react-router-dom";
import {
  getAgentInventoryList,
  saveAgentClient,
} from "../services/agentClientsService";
import {
  getInventoryById,
  deleteMyInventory,
} from "../services/inventoryService";
import { getMyMlsListings } from "@/services/v2/mlsListings";
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import SelectDropDown from "../components/Common/SelectDropDown";
import TextBox from "../components/Common/TextBox";
import { toast } from "react-toastify";
import {
  getLocalStorage,
  setLocalStorage,
} from "../helpers/common";
import { pageURLs } from "@/constants";
import {
  USER_INFO_KEY,
} from "../constants/common";
import JoyRide, { STATUS } from "react-joyride";
import DocumentationTutorial from "../components/Tutorial/DocumentationTutorial";
import { DashboardSummaryWidget, AgentInventoryListing } from "@/components";
import { saveTutorialStatus } from "../services/commonServices";
import IntroductionTutorialIconStep1 from "../images/tut-quickview-icon.svg";
import IntroductionTutorialIconStep2 from "../images/tut-performing-icon.svg";
import IntroductionTutorialIconStep3 from "../images/tut-easilyadd-icon.svg";
import _ from "lodash";
import { PageWrapper } from "@/components/PageWrapper";
import { useMLSAgentAtGitchaHomeRedirect } from "@/hooks/useMLSAgentAtGitchaRedirect";
import useLoading from "@/hooks/useLoading";
import { useAppConfig } from "@/appConfig";
import { useSearchParams } from "react-router-dom";
import { useCurrentWebsite } from "@/hooks/useCurrentWebsite";
import DashboardEmptyState from "@/components/AgentDashboard/DashboardEmptyState";
import InventoryTableSkeleton from "@/components/AgentDashboard/v2/InventoryTableSkeleton";

const VALID_INNER_TABS = ["prospects", "mls-active"];

const MLS_SORT_MAP = {
  date: { asc: "OLDEST", desc: "NEWEST" },
  price: { asc: "PRICE_ASC", desc: "PRICE_DESC" },
  potentialbuyers: { asc: "MATCHES_ASC", desc: "MATCHES_DESC" },
};

const MLS_STATUS_OPTIONS = [
  { value: "all", label: "All Statuses" },
  { value: "active", label: "Active" },
  { value: "under_contract", label: "Under Contract" },
  { value: "pending", label: "Pending" },
  { value: "closed", label: "Closed" },
  { value: "expired", label: "Expired" },
  { value: "withdrawn", label: "Withdrawn" },
];

const AgentMyInventory = () => {
  const { startLoading, stopLoading } = useLoading();
  const { flags } = useAppConfig();
  const showMlsListings = !!flags?.mlsListings;
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const initialInnerTab =
    showMlsListings && VALID_INNER_TABS.includes(tabParam)
      ? tabParam
      : "prospects";
  const [innerTab, setInnerTab] = useState(initialInnerTab);
  const isMlsTab = innerTab === "mls-active";

  const handleInnerTabChange = (key) => {
    if (!key) return;
    setInnerTab(key);
    setSearchValue(null);
    cancelMlsSearch();
    const nextParams = new URLSearchParams(searchParams);
    if (key === "prospects") {
      nextParams.delete("tab");
    } else {
      nextParams.set("tab", key);
    }
    setSearchParams(nextParams, { replace: true });
  };


  const tutorialPage = 4; //Agent Dashboard
  const navigate = useNavigate();
  const { isGitchaDefaultWebsite } = useCurrentWebsite();
  const summaryWidgetRef = useRef(null);
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);
  // Gitcha default now shows only the "Potential Sellers" tab, so default its
  // filter accordingly. BLS keeps "all" (its non-MLS tabs don't drive filterType).
  const [filterType, setFilterType] = useState(
    showMlsListings ? "all" : "potential",
  );
  const [searchValue, setSearchValue] = useState(null);
  const queryClient = useQueryClient();
  const [deleteModalShow, setDeleteModalShow] = useState(false);
  const userDetailsFromStore = useSelector(
    (state) => state.userProfile.userDetails,
  );
  const [itemId, setItemId] = useState(0);
  const CLIENT_TAB = "client";
  const INVENTORY_TAB = "inventory";
  const WANT_AD_TAB = "wantad";
  const [activeTab, setActiveTab] = useState(CLIENT_TAB);
  const [displayTutorial, setDisplayTutorial] = useState(false);
  const [tutorialAccept, setTutorialAccept] = useState(false);


  const [inventorySort, setInventorySort] = useState({
    order: "asc",
    orderBy: "date",
  });
  const [mlsSort, setMlsSort] = useState({
    order: "desc",
    orderBy: "date",
  });

  const [inventoryPage, setInventoryPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  const [mlsPage, setMlsPage] = useState(1);
  const [mlsStatus, setMlsStatus] = useState("all");

  const [debouncedMlsSearch, setDebouncedMlsSearch] = useState("");
  const mlsSearchTimerRef = useRef(null);

  // Debounced from the search handler rather than an effect on searchValue, so
  // the MLS query key only moves once the agent stops typing.
  const scheduleMlsSearch = (value) => {
    clearTimeout(mlsSearchTimerRef.current);
    mlsSearchTimerRef.current = setTimeout(() => {
      setDebouncedMlsSearch((value || "").trim());
      setMlsPage(1);
    }, 300);
  };

  const cancelMlsSearch = () => {
    clearTimeout(mlsSearchTimerRef.current);
    setDebouncedMlsSearch("");
    setMlsPage(1);
  };

  const mlsSortType =
    MLS_SORT_MAP[mlsSort.orderBy]?.[mlsSort.order] ?? "NEWEST";

  const mlsQuery = useQuery({
    queryKey: [
      "myMlsListings",
      { page: mlsPage, pageSize, sortType: mlsSortType, search: debouncedMlsSearch, status: mlsStatus },
    ],
    queryFn: async () => {
      const response = await getMyMlsListings({
        page: mlsPage,
        limit: pageSize,
        sortType: mlsSortType,
        ...(debouncedMlsSearch ? { search: debouncedMlsSearch } : {}),
        ...(mlsStatus && mlsStatus !== "all" ? { status: mlsStatus } : {}),
      });
      return response?.data || {};
    },
    enabled: showMlsListings && isMlsTab,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  const mlsList = useMemo(
    () => (Array.isArray(mlsQuery.data?.data) ? mlsQuery.data.data : []),
    [mlsQuery.data],
  );
  const mlsTotalCount = mlsQuery.data?.totalCount || 0;
  const mlsHasNextPage = mlsPage * pageSize < mlsTotalCount;
  const mlsItemsForTable = useMemo(
    () => mlsList.map((r) => ({ ...r, rowType: "mls" })),
    [mlsList],
  );

  // Private (non-MLS) inventory list. Refetches automatically when the query
  // key changes (search / sort / filter / page) and after a mutation
  // invalidates ["agentInventory"].
  const inventoryQuery = useQuery({
    queryKey: [
      "agentInventory",
      {
        search: searchValue ?? "",
        sortBy: inventorySort.orderBy,
        sortDir: inventorySort.order,
        filterType,
        page: inventoryPage,
        pageSize,
      },
    ],
    queryFn: async () => {
      const response = await getAgentInventoryList({
        sortBy: inventorySort.orderBy,
        search: searchValue,
        sortDir: inventorySort.order,
        page: inventoryPage,
        filterType,
        limit: pageSize,
      });
      if (response?.errorCode !== 0) {
        throw new Error(response?.message || "Failed to fetch inventory");
      }
      const result = _.get(response, "result", null);
      const list = result?.list ?? result ?? [];
      const totalCount = result?.totalCount ?? list.length;
      return { list, totalCount };
    },
    enabled: !isMlsTab,
    placeholderData: keepPreviousData,
    // Inventory is created in a separate flow that can't invalidate this cache,
    // so always refetch on mount to pick up a freshly created item (otherwise
    // the global 5-min staleTime serves a stale empty list).
    refetchOnMount: "always",
  });

  const itemList = inventoryQuery.data?.list ?? [];
  // Derived flags preserve the names the rest of the page already uses.
  const loading = inventoryQuery.isFetching;
  const showNoResult = inventoryQuery.isSuccess;
  const hasNextPage =
    (inventoryQuery.data?.totalCount ?? 0) > inventoryPage * pageSize;

  const handleMlsSort = (sortKey) => {
    if (!MLS_SORT_MAP[sortKey]) return;
    setMlsSort((prev) => ({
      order: prev.orderBy === sortKey && prev.order === "asc" ? "desc" : "asc",
      orderBy: sortKey,
    }));
    setMlsPage(1);
  };

  const handleMlsStatusChange = (option) => {
    setMlsStatus(option?.value || "all");
    setMlsPage(1);
  };
  const mlsStatusValue =
    MLS_STATUS_OPTIONS.find((o) => o.value === mlsStatus) || MLS_STATUS_OPTIONS[0];

  const addClientMutation = useMutation({
    mutationFn: (data) => saveAgentClient(data),
    onMutate: () => startLoading("addClient"),
    onSuccess: (response) => {
      if (response.errorCode !== 0) return;

      const userInfo = getLocalStorage(USER_INFO_KEY);
      userInfo.userInfo["ClientCount"] = userInfo.userInfo?.ClientCount
        ? userInfo.userInfo?.ClientCount + 1
        : 1;
      setLocalStorage(USER_INFO_KEY, userInfo);

      if (response.result.agentExist) {
        toast.error(response.message);
        return;
      }

      toast.success("Client added successfully", { autoClose: 1000 });
      setShow(false);
      setInventoryPage(1);
      queryClient.invalidateQueries({ queryKey: ["agentInventory"] });
    },
    onError: (err) => {
      console.log("Error", err);
    },
    onSettled: () => stopLoading("addClient"),
  });

  const addClientData = (data) => {
    addClientMutation.mutate(data);
  };

  /**
   * on searching the group
   * @param event any
   */
  const onSearch = (event) => {
    event.preventDefault();
    const value = event.target.value;
    setSearchValue(value);
    setInventoryPage(1);
    if (isMlsTab) {
      scheduleMlsSearch(value);
    }
  };

  /**
   * To prevent the enter key page reloading
   * @param event any
   */
  const onKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
    }
  };

  const handleInventorySort = (sortKey) => {
    setInventorySort((prevSort) => ({
      order:
        prevSort.order === "asc" && prevSort.orderBy === sortKey
          ? "desc"
          : "asc",
      orderBy: sortKey,
    }));

    setInventoryPage(1);
  };

  const onItemDelete = (itemId) => {
    setItemId(itemId);
    setDeleteModalShow(true);
  };

  const onItemEdit = (itemId) => {
    getDetails(itemId);
  };
  /**
   * To get inventory details and navigate to edit screen
   * @param {*} itemId
   */
  const getDetails = async (itemId) => {
    if (!itemId) {
      navigate(pageURLs.agentCreatewantad);
      return;
    }

    try {
      const response = await queryClient.fetchQuery({
        queryKey: ["inventoryById", itemId],
        queryFn: () => getInventoryById(itemId),
        // The edit form is seeded from this payload, so it must never open on a
        // cached copy — override the global 5-min stale window.
        staleTime: 0,
      });
      const responseResult = _.get(response, "result", null);
      if (response.errorCode === 0 && responseResult) {
        navigate(pageURLs.agentCreateInventory, {
          state: { inventoryId: itemId, data: responseResult },
        });
      } else {
        console.log("Something went wrong");
      }
    } catch (err) {
      console.log("Error", err);
    }
  };

  const hideDeleteModal = () => {
    setDeleteModalShow(false);
  };
  /**
   * To handle deletion if inventory/wantad
   */
  const deleteInventoryMutation = useMutation({
    mutationFn: (id) => deleteMyInventory(id),
    onMutate: () => startLoading("deleteInventory"),
    onSuccess: (response, id) => {
      const responseResult = _.get(response, "result", null);
      if (response.errorCode === 0 && responseResult) {
        toast.success("Your inventory has been successfully deleted.", {
          autoClose: 1000,
        });
        setDeleteModalShow(false);
        queryClient.invalidateQueries({ queryKey: ["agentInventory"] });
        queryClient.removeQueries({ queryKey: ["inventoryById", id] });
        summaryWidgetRef.current?.refetch();
      } else {
        console.log("Something went wrong");
      }
    },
    onError: (err) => {
      console.log("Error", err);
    },
    onSettled: () => stopLoading("deleteInventory"),
  });

  const handleDeleteItem = () => {
    if (itemId) {
      deleteInventoryMutation.mutate(itemId);
    }
  };

  const dashboardHeaderSection = (props) => {
    const { placeHolder } = props;
    return (
      <div className="dashboard-top-bar">
        <div className="dashboard-search">
          <TextBox
            type="text"
            className="form-control"
            placeholder={placeHolder}
            onChange={onSearch}
            onKeyDown={onKeyDown}
            value={searchValue ?? ""}
            notShowError
          ></TextBox>

          <span className="search-icon">search</span>
        </div>

        <Dropdown as={ButtonGroup}>
          <Button
            variant="primary"
            className="split-btn-margin text-nowrap ms-3"
            onClick={() => {
              window.scrollTo({ top: 0 });
              navigate(pageURLs.agentCreateInventory);
            }}
          >
            Add Prospect's Inventory
          </Button>

            <Dropdown.Toggle split variant="primary" id="dropdown-split-basic" />

            <Dropdown.Menu>
              <Dropdown.Item onClick={() => handleShow()}>
                <span>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8 0.0898438C7.52138 0.0898438 7.04293 0.245532 6.64746 0.556966H6.64665L1.69141 4.4624C0.939922 5.05464 0.5 5.95958 0.5 6.91683V14.4535C0.5 15.2513 1.16044 15.9118 1.95833 15.9118H14.0417C14.8396 15.9118 15.5 15.2513 15.5 14.4535V6.91683C15.5 5.95958 15.0601 5.05464 14.3086 4.4624L14.25 4.4152V1.125C14.25 0.959245 14.1841 0.800283 14.0669 0.683077C13.9497 0.56587 13.7908 0.500017 13.625 0.5H11.5417C11.3759 0.500017 11.2169 0.56587 11.0997 0.683077C10.9825 0.800283 10.9167 0.959245 10.9167 1.125V1.78906L9.35254 0.556966C8.95707 0.245532 8.47862 0.0898438 8 0.0898438ZM8 1.33659C8.20408 1.33659 8.40823 1.40441 8.57943 1.53923L11.1551 3.56803C11.2473 3.64054 11.358 3.68566 11.4746 3.69824C11.5911 3.71082 11.7089 3.69035 11.8144 3.63917C11.9199 3.58798 12.0089 3.50815 12.0712 3.4088C12.1335 3.30945 12.1666 3.19458 12.1667 3.07731V1.75H13V4.71875C13.0001 4.81325 13.0216 4.9065 13.0629 4.9915C13.1042 5.0765 13.1642 5.15102 13.2384 5.20947L13.5355 5.44385C13.9873 5.79994 14.25 6.34158 14.25 6.91683V14.4535C14.25 14.5764 14.1646 14.6618 14.0417 14.6618H1.95833C1.8354 14.6618 1.75 14.5764 1.75 14.4535V6.91683C1.75 6.34158 2.01267 5.79994 2.46452 5.44385L7.42057 1.53923C7.59177 1.40441 7.79592 1.33659 8 1.33659ZM4.04167 7.99512C3.87591 7.99513 3.71695 8.06099 3.59974 8.17819C3.48254 8.2954 3.41668 8.45436 3.41667 8.62012V11.9535C3.41668 12.1192 3.48254 12.2782 3.59974 12.3954C3.71695 12.5126 3.87591 12.5784 4.04167 12.5785H6.54167C6.70742 12.5784 6.86638 12.5126 6.98359 12.3954C7.1008 12.2782 7.16665 12.1192 7.16667 11.9535V8.62012C7.16665 8.45436 7.1008 8.2954 6.98359 8.17819C6.86638 8.06099 6.70742 7.99513 6.54167 7.99512H4.04167ZM9.45833 7.99512C9.29258 7.99513 9.13362 8.06099 9.01641 8.17819C8.8992 8.2954 8.83335 8.45436 8.83333 8.62012V11.9535C8.83335 12.1192 8.8992 12.2782 9.01641 12.3954C9.13362 12.5126 9.29258 12.5784 9.45833 12.5785H11.9583C12.1241 12.5784 12.283 12.5126 12.4003 12.3954C12.5175 12.2782 12.5833 12.1192 12.5833 11.9535V8.62012C12.5833 8.45436 12.5175 8.2954 12.4003 8.17819C12.283 8.06099 12.1241 7.99513 11.9583 7.99512H9.45833ZM4.66667 9.24512H5.91667V11.3285H4.66667V9.24512ZM10.0833 9.24512H11.3333V11.3285H10.0833V9.24512Z"
                      fill={themeColors.neutral900}
                    />
                  </svg>
                </span>
                Add Client
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() => navigate(pageURLs.agentCreatewantadMainScreen)}
              >
                <span>
                  <svg
                    width="18"
                    height="16"
                    viewBox="0 0 18 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M1.26407 1.05319C1.49849 0.818769 1.81643 0.687073 2.14795 0.687073H15.8979C16.2295 0.687073 16.5474 0.818769 16.7818 1.05319C17.0163 1.28761 17.1479 1.60555 17.1479 1.93707V14.4371C17.1479 14.6537 17.0358 14.8549 16.8515 14.9687C16.6673 15.0826 16.4372 15.093 16.2434 14.9961L14.0229 13.8858L11.8025 14.9961C11.6265 15.0841 11.4194 15.0841 11.2434 14.9961L9.02295 13.8858L6.80246 14.9961C6.6265 15.0841 6.41939 15.0841 6.24344 14.9961L4.02295 13.8858L1.80246 14.9961C1.60871 15.093 1.37863 15.0826 1.19437 14.9687C1.01011 14.8549 0.897949 14.6537 0.897949 14.4371V1.93707C0.897949 1.60555 1.02965 1.28761 1.26407 1.05319ZM15.8979 1.93707L2.14795 1.93707L2.14795 13.4258L3.74344 12.6281C3.9194 12.5401 4.1265 12.5401 4.30246 12.6281L6.52295 13.7383L8.74344 12.6281C8.9194 12.5401 9.1265 12.5401 9.30246 12.6281L11.5229 13.7383L13.7434 12.6281C13.9194 12.5401 14.1265 12.5401 14.3025 12.6281L15.8979 13.4258V1.93707Z"
                      fill={themeColors.neutral780}
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M9.64795 6.31207C9.64795 5.9669 9.92777 5.68707 10.2729 5.68707H14.0229C14.3681 5.68707 14.6479 5.9669 14.6479 6.31207C14.6479 6.65725 14.3681 6.93707 14.0229 6.93707H10.2729C9.92777 6.93707 9.64795 6.65725 9.64795 6.31207Z"
                      fill={themeColors.neutral780}
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M9.64795 8.81207C9.64795 8.4669 9.92777 8.18707 10.2729 8.18707H14.0229C14.3681 8.18707 14.6479 8.4669 14.6479 8.81207C14.6479 9.15725 14.3681 9.43707 14.0229 9.43707H10.2729C9.92777 9.43707 9.64795 9.15725 9.64795 8.81207Z"
                      fill={themeColors.neutral780}
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M3.39795 5.06207C3.39795 4.7169 3.67777 4.43707 4.02295 4.43707H7.77295C8.11813 4.43707 8.39795 4.7169 8.39795 5.06207V10.0621C8.39795 10.4073 8.11813 10.6871 7.77295 10.6871H4.02295C3.67777 10.6871 3.39795 10.4073 3.39795 10.0621V5.06207ZM4.64795 5.68707V9.43707H7.14795V5.68707H4.64795Z"
                      fill={themeColors.neutral780}
                    />
                  </svg>
                </span>
                Post a want-listing
              </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>
    );
  };

  const emptyItemHtml = () => {
    if (loading || !showNoResult) return null;

    const isFirstUse =
      !searchValue &&
      innerTab === "prospects" &&
      (itemList == null || itemList.length === 0);

    if (isFirstUse && !isGitchaDefaultWebsite) {
      return (
        <DashboardEmptyState
          variant="inventory"
          onCta={() => {
            window.scrollTo({ top: 0 });
            navigate(pageURLs.agentCreateInventory);
          }}
        />
      );
    }

    return (
      <div className="no-result-wrap no-result-padding">
        <h3>No results found</h3>
        <p>
          We couldn't find any listings that match your filters and search
          terms. Try a different search or reset all filters.
        </p>
      </div>
    );
  };

  let deleteButtonProps = {};

  if (activeTab === INVENTORY_TAB) {
    _.set(
      deleteButtonProps,
      "title",
      "Are you sure you want to permanently delete your property?",
    );
    _.set(
      deleteButtonProps,
      "message",
      "Deleting this property from your inventory will remove any pending and accepted introductions sent by you.",
    );
    _.set(deleteButtonProps, "btnText", " Delete Property");
  }

  //Tutorial documentation section
  //Defining tutorial steps
  const [tutorialSteps, setTutorialSteps] = useState([
    {
      target: ".tutorialRow",
      disableBeacon: true,
      placement: "left",
      title: "Quickly view all of your clients information",
      icon: IntroductionTutorialIconStep1,
      content:
        "You can view a summary here or select your client to view their full set of inventory and want listings.",
      hideCloseButton: true,
      showProgress: true,
      showSkipButton: true,
    },
    {
      target: ".tutorialRow",
      disableBeacon: true,
      placement: "top",
      title: "Figure out which listings / properties are performing",
      icon: IntroductionTutorialIconStep2,
      content:
        "This will help you modify your listings and inventory to figure out what strategies are worth replicating.",
      hideCloseButton: true,
      showProgress: true,
      showSkipButton: true,
    },
    {
      target: "#clientAdd",
      disableBeacon: true,
      placement: "left",
      title: "Easily add",
      icon: IntroductionTutorialIconStep3,
      content:
        "New clients, new inventory, and new want listings from one spot",
      hideCloseButton: true,
      showProgress: true,
      showSkipButton: true,
      spotlightPadding: false,
    },
  ]);
  //Tutorial tooltip
  const TutorialToolTip = ({
    continuous,
    index,
    step,
    backProps,
    closeProps,
    primaryProps,
    tooltipProps,
    size,
    isLastStep,
    skipProps,
  }) => (
    <>
      <div {...tooltipProps}>
        <DocumentationTutorial
          continuous={continuous}
          index={index}
          step={step}
          backProps={backProps}
          skipProps={skipProps}
          primaryProps={primaryProps}
          isLastStep={isLastStep}
          size={size}
          finalStepBtn={"Done"}
        />
      </div>
    </>
  );
  //Tutorial status call back
  const handleJoyrideCallback = async (data) => {
    const { action, index, status, type, lifecycle } = data;
    if (
      [STATUS.FINISHED, STATUS.SKIPPED].includes(status) &&
      lifecycle == "complete"
    ) {
      setTutorialAccept(true);
      saveTutorialStatusToLocal(tutorialPage);
      await saveTutorialStatus(tutorialPage, 1); //Save status
      setDisplayTutorial(false);
    }
    if (action == "update" && index == 2 && status == "running") {
      document.querySelector("#dropdown-split-basic").click();
    }
  };

  const handleTabChange = (_key) => {
    setFilterType(_key);
    setInventoryPage(1);
  };

  const onNextPage = () => {
    if (hasNextPage) {
      setInventoryPage((prev) => prev + 1);
    }
  };

  const onPrevPage = () => {
    if (inventoryPage > 1) {
      setInventoryPage((prev) => prev - 1);
    }
  };

  useMLSAgentAtGitchaHomeRedirect();

  const widgetScope = isMlsTab ? "mls" : "inventory";

  const mlsInitialLoading = mlsQuery.isPending && !mlsQuery.data;
  const mlsHasRows = mlsItemsForTable.length > 0;
  const mlsErrorOnly = mlsQuery.isError && !mlsHasRows;

  return (
    <>
      {displayTutorial && itemList !== null && itemList?.length ? (
        <JoyRide
          steps={tutorialSteps}
          tooltipComponent={TutorialToolTip}
          continuous
          callback={handleJoyrideCallback}
        />
      ) : (
        ""
      )}
      <PageWrapper headerSidebarProps={{ tutorialAccept }}>
        <div className="m-0">
          <div className="main-content p-0 pb-4 agent-no-login-content">
            <div className="agent-dashboard-wrap">
              <h2 className="mb-3">My Inventory</h2>

              <DashboardSummaryWidget ref={summaryWidgetRef} scope={widgetScope} />
              {(() => {
                const inventoryTable = loading ? (
                  <InventoryTableSkeleton />
                ) : (
                  <AgentInventoryListing
                    showNoResult={showNoResult}
                    handleSort={handleInventorySort}
                    itemList={itemList}
                    onItemEdit={onItemEdit}
                    onItemDelete={onItemDelete}
                    sort={inventorySort}
                    emptyItemHtml={emptyItemHtml}
                    loading={loading}
                    currentPage={inventoryPage}
                    hasNextPage={hasNextPage}
                    onNextPage={onNextPage}
                    onPrevPage={onPrevPage}
                    pageSize={pageSize}
                    onPageSizeChange={(size) => { setPageSize(size); setInventoryPage(1); setMlsPage(1); }}
                  />
                );

                const localInventoryBody = (
                  <div className="horizontal-tab-wrap">
                    {dashboardHeaderSection({
                      placeHolder: "Search for address or client",
                      activeTab: INVENTORY_TAB,
                    })}
                    {inventoryTable}
                  </div>
                );

                if (!showMlsListings) {
                  return (
                    <>
                      <div className="agent-my-inventory-wrap">
                        <div className="tab-wrap">
                          <Tabs
                            defaultActiveKey="potential"
                            onSelect={handleTabChange}
                          >
                            <Tab
                              eventKey={"potential"}
                              title={"Potential Sellers"}
                            ></Tab>
                          </Tabs>
                        </div>
                      </div>
                      {localInventoryBody}
                    </>
                  );
                }

                return (
                  <>
                    <div className="agent-my-inventory-wrap">
                      <div className="tab-wrap">
                        <Tabs activeKey={innerTab} onSelect={handleInnerTabChange}>
                          <Tab eventKey="prospects" title="Prospects Inventory" />
                          <Tab eventKey="mls-active" title="My Active MLS Listings" />
                        </Tabs>
                      </div>
                    </div>

                    {isMlsTab ? (() => {
                      if (mlsErrorOnly) {
                        return (
                          <div className="no-result-wrap no-result-padding">
                            <h3>Couldn't load your MLS listings</h3>
                            <p>Something went wrong. Please try again.</p>
                            <button
                              className="btn btn-primary mt-2"
                              onClick={() => mlsQuery.refetch()}
                            >
                              Retry
                            </button>
                          </div>
                        );
                      }
                      if (mlsInitialLoading) {
                        return (
                          <div className="horizontal-tab-wrap">
                            {dashboardHeaderSection({
                              placeHolder: "Search for address",
                            })}
                            <InventoryTableSkeleton />
                          </div>
                        );
                      }
                      if (!mlsHasRows && !debouncedMlsSearch && mlsStatus === "all") {
                        return (
                          <div className="horizontal-tab-wrap">
                            {dashboardHeaderSection({
                              placeHolder: "Search for address",
                            })}
                            <div className="no-result-wrap no-result-padding">
                              <h3>Your MLS listings haven't loaded yet</h3>
                              <p>
                                We haven't matched your MLS profile to Gitcha
                                yet. Once your MLS sign-in is linked, your
                                listings will appear here automatically.
                              </p>
                            </div>
                          </div>
                        );
                      }
                      return (
                        <div className="horizontal-tab-wrap">
                          {dashboardHeaderSection({
                            placeHolder: "Search for address",
                          })}
                          <div className="mls-status-filter d-flex align-items-center mb-3" style={{ gap: 8, maxWidth: 340 }}>
                            <label className="mb-0" style={{ fontWeight: 500, whiteSpace: "nowrap" }}>Property Status:</label>
                            <div style={{ flex: 1, minWidth: 180 }}>
                              <SelectDropDown
                                options={MLS_STATUS_OPTIONS}
                                value={mlsStatusValue}
                                onChange={handleMlsStatusChange}
                                classNamePrefix="mls-status-select"
                                errorClass="d-none"
                              />
                            </div>
                          </div>
                          <div style={{ opacity: mlsQuery.isFetching ? 0.7 : 1, transition: "opacity 120ms ease" }}>
                            <AgentInventoryListing
                              mode="mls"
                              showNoResult={true}
                              handleSort={handleMlsSort}
                              itemList={mlsItemsForTable}
                              onItemEdit={() => { }}
                              onItemDelete={() => { }}
                              sort={mlsSort}
                              emptyItemHtml={emptyItemHtml}
                              loading={false}
                              currentPage={mlsPage}
                              hasNextPage={mlsHasNextPage}
                              onNextPage={() => mlsHasNextPage && setMlsPage((p) => p + 1)}
                              onPrevPage={() => mlsPage > 1 && setMlsPage((p) => p - 1)}
                              pageSize={pageSize}
                              onPageSizeChange={(size) => { setPageSize(size); setMlsPage(1); setInventoryPage(1); }}
                            />
                          </div>
                        </div>
                      );
                    })() : (
                      localInventoryBody
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      </PageWrapper>

      <AddNewClientModal
        show={show}
        onHide={handleClose}
        addClient={addClientData}
      />

      <DeleteModal
        show={deleteModalShow}
        onHide={() => hideDeleteModal()}
        title={"Are you sure you want to permanently delete your property?"}
        message={
          "Deleting this property from your inventory will remove any pending and accepted introductions sent by you."
        }
        buttonText={" Delete Property"}
        handleConfirm={() => handleDeleteItem()}
      />
    </>
  );
};

export default AgentMyInventory;
